import { motion, useReducedMotion } from 'framer-motion';
import React, { useLayoutEffect, useMemo, useRef, useState, useEffect, useCallback } from 'react';
import { ChevronDown } from 'lucide-react';
import type { Service } from '../../types';

/* -------------------- 3D CHARTS -------------------- */
type ChartTransform = {
  src: string;
  x: string | number;
  y?: string | number;
  z: number;
  rotateX: number;
  rotateY: number;
  rotateZ?: number;
  scale: number;
  zIndex: number;
};


function getCascadeCharts(serviceId: string): ChartTransform[] {
  // Only ORION uses charts, others show "Coming Soon"
  const pairByService: Record<string, { front: string; back: string }> = {
    orion: { front: '/media/chart_orion_4.png', back: '/media/chart_orion_5.png' },
  };

  const chosen = pairByService[serviceId] || { front: '/media/chart_orion_2.png', back: '/media/chart_orion_3.png' };

  return [
    { src: chosen.back, x: '25%', y: '-10%', z: -25, rotateX: 0, rotateY: -30, scale: 1, zIndex: 10 },
    { src: chosen.front, x: '-25%', y: '10%', z: 25, rotateX: 0, rotateY: -30, scale: 1, zIndex: 20 },
  ];
}

const ComingSoonDisplay: React.FC = () => {
  return (
    <div className="relative w-full h-full flex items-center justify-center">
      <motion.h3
        className="text-2xl md:text-3xl font-semibold text-white/90"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.4, 0, 0.2, 1] }}
      >
        Coming Soon
      </motion.h3>
    </div>
  );
};

const OrionShowcase: React.FC<{ serviceId: string }> = ({ serviceId }) => {
  // Show "Coming Soon" for services other than ORION
  if (serviceId !== 'orion') {
    return <ComingSoonDisplay />;
  }

  const charts = getCascadeCharts(serviceId);
  return (
    <div className="relative w-full h-full flex items-center justify-center" style={{ perspective: '1200px' }}>
      {charts.map((chart, i) => (
        <motion.div
          key={`cascade-${i}`}
          className="absolute w-[70%] md:w-[65%] lg:w-[60%] aspect-[16/10]"
          style={{ transformStyle: 'preserve-3d', zIndex: chart.zIndex }}
          initial={{ opacity: 0, y: 30, scale: 0.9 }}
          animate={{
            opacity: 1,
            y: chart.y || 0,
            x: chart.x,
            z: chart.z,
            rotateY: chart.rotateY,
            rotateX: chart.rotateX,
            rotateZ: chart.rotateZ || 0,
            scale: chart.scale,
          }}
          transition={{ type: 'spring', stiffness: 150, damping: 20, delay: i * 0.1 }}
        >
          {/* Main chart container with adjusted shadows */}
          <div 
            className="absolute inset-0 rounded-lg overflow-hidden bg-white/5 backdrop-blur-sm border border-white/10"
            style={{
              filter: chart.zIndex === 20 ? 
                'drop-shadow(10px 5px 20px rgba(0,0,0,0.6)) drop-shadow(20px 10px 30px rgba(0,0,0,0.4))' : 
                'drop-shadow(0 15px 25px rgba(0,0,0,0.4))',
              boxShadow: chart.zIndex === 20 ? 
                '10px 5px 20px rgba(0,0,0,0.6), 20px 10px 30px rgba(0,0,0,0.4)' : 
                '0 20px 35px rgba(0,0,0,0.3)'
            }}
          >
            <img src={chart.src} alt={`Orion chart ${i + 1}`} className="absolute inset-0 w-full h-full object-cover" />
          </div>
          
          {/* Reflection effect positioned below the chart */}
          <div 
            className="absolute rounded-lg overflow-hidden opacity-30 pointer-events-none"
            style={{
              top: '100%',
              left: '0',
              width: '100%',
              height: '60%',
              transform: 'scaleY(-1)',
              zIndex: -2
            }}
          >
            <div 
              className="absolute inset-0 bg-white/5 backdrop-blur-sm border border-white/10"
              style={{
                maskImage: 'linear-gradient(to bottom, rgba(0,0,0,0.5) 0%, rgba(0,0,0,0.2) 50%, transparent 100%)',
                WebkitMaskImage: 'linear-gradient(to bottom, rgba(0,0,0,0.5) 0%, rgba(0,0,0,0.2) 50%, transparent 100%)'
              }}
            >
              <img 
                src={chart.src} 
                alt=""
                className="absolute inset-0 w-full h-full object-cover opacity-50"
              />
            </div>
          </div>
        </motion.div>
      ))}
    </div>
  );
};

/* -------------------- Height measurer -------------------- */
function useAnimatedHeights({
  fullText,
  collapsedText,
  containerRef,
  fontClass = 'text-white/60 text-sm md:text-base leading-relaxed',
  maxWidth = '100%',
}: {
  fullText: string;
  collapsedText: string;
  containerRef: React.RefObject<HTMLDivElement>;
  fontClass?: string;
  maxWidth?: string;
}) {
  const sizerRef = useRef<HTMLDivElement | null>(null);
  const [collapsedHeight, setCollapsedHeight] = useState<number>(0);
  const [expandedHeight, setExpandedHeight] = useState<number>(0);

  const measure = useCallback(() => {
    if (!sizerRef.current || !containerRef.current) return;
    const sizer = sizerRef.current;

    // Match sizer width to container's actual width
    const containerWidth = containerRef.current.offsetWidth;
    sizer.style.width = containerWidth ? `${containerWidth}px` : '100%';

    // Measure collapsed text
    sizer.textContent = collapsedText;
    const collapsed = sizer.scrollHeight;

    // Measure expanded text
    sizer.textContent = fullText;
    const expanded = sizer.scrollHeight;

    // Add padding to ensure full visibility
    setCollapsedHeight(collapsed + 20);
    setExpandedHeight(expanded + 20);
  }, [collapsedText, fullText, containerRef]);

  useLayoutEffect(() => {
    if (typeof document === 'undefined') return;

    const currentContainer = containerRef.current;

    const el = document.createElement('div');
    sizerRef.current = el;
    Object.assign(el.style, {
      position: 'absolute',
      visibility: 'hidden',
      pointerEvents: 'none',
      top: '-10000px',
      left: '0px',
      width: currentContainer?.offsetWidth ? `${currentContainer.offsetWidth}px` : '100%',
      maxWidth,
      whiteSpace: 'normal',
      wordWrap: 'break-word',
      lineHeight: '1.5',
      padding: '0 16px',
    } as CSSStyleDeclaration);
    el.className = fontClass;
    document.body.appendChild(el);

    measure();

    let ro: ResizeObserver | null = null;
    if (currentContainer && typeof window !== 'undefined' && (window as { ResizeObserver?: typeof ResizeObserver }).ResizeObserver) {
      ro = new ResizeObserver(() => measure());
      ro.observe(currentContainer);
    }

    const onResize = () => measure();
    window.addEventListener('resize', onResize);

    return () => {
      window.removeEventListener('resize', onResize);
      if (ro && currentContainer) ro.disconnect();
      if (sizerRef.current) {
        document.body.removeChild(sizerRef.current);
        sizerRef.current = null;
      }
    };
  }, [fullText, collapsedText, fontClass, maxWidth, measure, containerRef]);

  // Recalculate heights when text or container size changes
  useEffect(() => {
    measure();
  }, [fullText, collapsedText, measure]);

  return { collapsedHeight, expandedHeight };
}

/* -------------------- SERVICE CARD -------------------- */
interface ServiceCardProps {
  service: Service;
}

const ServiceCard: React.FC<ServiceCardProps> = ({ service }) => {
  const prefersReduced = useReducedMotion();
  const textContainerRef = useRef<HTMLDivElement>(null);
  const [isExpanded, setIsExpanded] = useState(false);

  // Extract short introduction (first sentence must be fully visible)
  const { shortText, fullText, hasMore } = useMemo(() => {
    const desc = service.description || '';
    
    // Split by period to get the first sentence
    const sentences = desc.split(/\.(\s|$)/);
    let firstSentence = sentences[0].trim();
    if (firstSentence && !firstSentence.endsWith('.')) firstSentence += '.';

    if (sentences.length > 1 && firstSentence) {
      return {
        shortText: firstSentence,
        fullText: desc,
        hasMore: true
      };
    }

    // If no clear sentence break, use the full text up to 200 characters without truncation
    if (desc.length <= 200) {
      return {
        shortText: desc,
        fullText: desc,
        hasMore: false
      };
    }

    // Fallback: use full first sentence and truncate only if necessary beyond that
    return {
      shortText: firstSentence || desc.substring(0, 180).trim() + '...',
      fullText: desc,
      hasMore: true
    };
  }, [service.description]);

  const { collapsedHeight, expandedHeight } = useAnimatedHeights({
    fullText,
    collapsedText: shortText,
    containerRef: textContainerRef,
    fontClass: 'text-white/60 text-sm md:text-base leading-relaxed',
    maxWidth: '100%',
  });

  return (
    <motion.div
      initial={{ opacity: 0, y: 60 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className="relative w-full max-w-6xl mx-auto overflow-hidden rounded-lg border border-white/10 bg-zinc-900/60"
      layout
      animate={{
        scale: isExpanded ? 1.02 : 1,
        boxShadow: isExpanded
          ? '0 25px 50px -12px rgba(0,0,0,0.4)'
          : '0 10px 15px -3px rgba(0,0,0,0.1)',
      }}
      transition={{
        duration: prefersReduced ? 0 : 0.6,
        ease: [0.4, 0, 0.2, 1],
      }}
    >
      {/* Ambient glow */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background: 'radial-gradient(circle at 0% 50%, rgba(6, 182, 212, 0.15) 0%, transparent 50%)',
        }}
      />

      <div className="relative z-10 grid lg:grid-cols-5 lg:gap-8">
        {/* LEFT: TEXT */}
        <div className="lg:col-span-2 flex flex-col justify-center p-8 md:p-10 lg:p-12">
          <h3 className="text-white font-semibold text-xl md:text-2xl">
            {service.title || 'Service'}
          </h3>
          <div className="w-1/3 h-px bg-primary-cyan/50 my-5" />

          {/* Animated text container */}
          <motion.div
            ref={textContainerRef}
            className="overflow-hidden w-full"
            animate={{
              height: isExpanded ? expandedHeight : collapsedHeight,
            }}
            transition={{
              duration: prefersReduced ? 0 : 0.4,
              ease: [0.4, 0, 0.2, 1],
            }}
            style={{
              height: isExpanded ? expandedHeight : collapsedHeight,
            }}
          >
            <div className="text-white/60 text-sm md:text-base leading-relaxed px-4">
              {isExpanded ? fullText : shortText}
            </div>
          </motion.div>

          {hasMore && (
            <button
              onClick={() => setIsExpanded(v => !v)}
              className="inline-flex items-center gap-2 text-primary-cyan hover:text-primary-cyan/80 transition-colors duration-300 mt-1 px-4"
            >
              <span className="text-sm font-medium">{isExpanded ? 'Read less' : 'Read more'}</span>
              <ChevronDown
                size={16}
                className={`transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`}
              />
            </button>
          )}
        </div>

        {/* RIGHT: VISUAL */}
        <div className="lg:col-span-3 min-h-[300px] md:min-h[360px] lg:min-h-[400px] p-6 lg:p-0">
          <OrionShowcase serviceId={service.id} />
        </div>
      </div>
    </motion.div>
  );
};

export default ServiceCard;